import { useCallback, useState } from "react";
import { getFeatureCost } from "../api/wallet.api";
import { emit } from "../lib/walletEvents";

// Hook: wrap an async credit-debiting action with a "confirm N credits" prompt.
//
// Usage in a component:
//
//   const { confirm, modalProps } = useCreditConfirm("match_summary_report", "match report");
//   <button onClick={() => confirm(() => generateMatchAIReport(matchId))}>Generate</button>
//   <CreditConfirmModal {...modalProps} />
//
// On confirm:
//   1. Closes the modal
//   2. Invokes the supplied async fn
//   3. Emits "wallet_changed" so the badge re-fetches
export default function useCreditConfirm(featureCode, featureLabel) {
  const [open, setOpen] = useState(false);
  const [cost, setCost] = useState(0);
  const [balance, setBalance] = useState(0);
  const [busy, setBusy] = useState(false);
  const [pendingFn, setPendingFn] = useState(null);

  const confirm = useCallback(async (fn) => {
    try {
      const res = await getFeatureCost(featureCode);
      // 2026-06-05 — B2B plans with skip_credit_gate=True don't debit.
      // Backend returns cost_credits=0 + skip_credit_gate=true. Skip
      // the modal entirely and run the action so the operator doesn't
      // see a confusing "Confirm 0 ZC" prompt or a misleading list
      // price for something that's actually free under their plan.
      const skipGate = !!res.data?.skip_credit_gate;
      const effectiveCost = res.data?.cost_credits ?? 0;
      if (skipGate || effectiveCost <= 0) {
        setBusy(true);
        try { await fn(); } finally {
          setBusy(false);
          emit("wallet_changed");
        }
        return;
      }
      setCost(effectiveCost);
      setBalance(res.data.balance_credits);
      setPendingFn(() => fn);
      setOpen(true);
    } catch (e) {
      // If cost-preview itself errors, fall through to the action and let the
      // 402 interceptor handle it. Better than blocking on a transient.
      setBusy(true);
      try { await fn(); } finally {
        setBusy(false);
        emit("wallet_changed");
      }
    }
  }, [featureCode]);

  const onConfirm = useCallback(async () => {
    setBusy(true);
    try {
      if (pendingFn) await pendingFn();
    } finally {
      setBusy(false);
      setOpen(false);
      setPendingFn(null);
      emit("wallet_changed");
    }
  }, [pendingFn]);

  const onCancel = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setPendingFn(null);
  }, [busy]);

  return {
    confirm,
    modalProps: { open, cost, balance, featureLabel, busy, onConfirm, onCancel },
  };
}
